import { ApiClient } from "./api"
import { DomHelper } from "./dom"

export class ReservationDetailHandler {
  constructor(element) {
    this.element = element
  }

  async showReservationDetail(cell) {
    const reservationId = cell.dataset.reservationId
    const reservationIds = cell.dataset.reservationIds

    if (reservationIds) {
      await this.showMultipleReservationDetails(cell, reservationIds)
    } else if (reservationId) {
      await this.showSingleReservationDetail(cell, reservationId)
    } else {
      console.error('No reservation ID found on cell')
    }
  }

  async showSingleReservationDetail(cell, reservationId) {
    try {
      const response = await ApiClient.get(`/admin/reservations/${reservationId}.json`)
      const data = await response.json()
      this.displayReservationPopover(cell, data)
    } catch (error) {
      console.error('Error fetching reservation details:', error)
    }
  }

  async showMultipleReservationDetails(cell, reservationIds) {
    try {
      const ids = reservationIds.split(',')
      const promises = ids.map(id => 
        ApiClient.get(`/admin/reservations/${id.trim()}.json`).then(response => response.json())
      )

      const reservations = await Promise.all(promises)
      this.displayMultipleReservationsPopover(cell, reservations)
    } catch (error) {
      console.error('Error fetching multiple reservation details:', error)
    }
  }

  displayReservationPopover(cell, reservation) {
    const popover = document.querySelector('.reservation-detail-popover')
    this.resetPopoverToSingleReservation(popover)

    popover.querySelector('.popover-header strong').textContent = `予約詳細 (ID: ${reservation.id})`
    popover.querySelector('.customer-name').textContent = reservation.customer.name
    popover.querySelector('.trainer-name').textContent = reservation.trainer.name
    popover.querySelector('.start-time').textContent = DomHelper.formatDateTime(reservation.start_time)
    popover.querySelector('.end-time').textContent = DomHelper.formatDateTime(reservation.end_time)
    popover.querySelector('.menu-name').textContent = reservation.menu.name

    const editBtn = popover.querySelector('.edit-reservation-btn')
    const deleteBtn = popover.querySelector('.delete-reservation-btn')
    editBtn.href = `/admin/reservations/${reservation.id}/edit`
    deleteBtn.href = `/admin/reservations/${reservation.id}`
    deleteBtn.dataset.method = 'delete'
    deleteBtn.dataset.turboConfirm = '本当に削除しますか？'

    // ダブルクリック防止
    deleteBtn.addEventListener('click', (e) => {
      if (deleteBtn.dataset.submitting === 'true') {
        e.preventDefault()
        return
      }
      deleteBtn.dataset.submitting = 'true'
      deleteBtn.textContent = '削除中...'
      deleteBtn.classList.add('disabled')
    })

    this.positionPopover(popover, cell)

    document.addEventListener('click', this.handleOutsideClick.bind(this), { once: true })
  }

  displayMultipleReservationsPopover(cell, reservations) {
    const popover = document.querySelector('.reservation-detail-popover')
    const content = this.generateMultipleReservationsHTML(reservations)

    const popoverContainer = popover.querySelector('.popover')
    popoverContainer.innerHTML = `
      <div class="popover-arrow"></div>
      <div class="popover-header">
        <strong>重複予約 (${reservations.length}件)</strong>
        <button class="btn-close float-end" type="button" data-action="click->reservation-detail-popover#close"></button>
      </div>
      <div class="popover-body">
        ${content}
      </div>
    `

    this.positionPopover(popover, cell)

    // 複数予約時の削除ボタンにダブルクリック防止を追加
    popover.querySelectorAll('.delete-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (btn.dataset.submitting === 'true') {
          e.preventDefault()
          return
        }
        btn.dataset.submitting = 'true'
        btn.textContent = '削除中...'
        btn.classList.add('disabled')
      })
    })

    document.addEventListener('click', this.handleOutsideClick.bind(this), { once: true })
  }

  generateMultipleReservationsHTML(reservations) {
    return reservations.map((reservation, index) => `
      <div class="reservation-item" style="border-bottom: 1px solid #eee; padding: 8px 0; ${index === reservations.length - 1 ? 'border-bottom: none;' : ''}">
        <div><strong>予約 ${index + 1}</strong></div>
        <div>ID: ${reservation.id}</div>
        <div>顧客: ${reservation.customer.name}</div>
        <div>トレーナー: ${reservation.trainer.name}</div>
        <div>開始: ${DomHelper.formatDateTime(reservation.start_time)}</div>
        <div>終了: ${DomHelper.formatDateTime(reservation.end_time)}</div>
        <div>メニュー: ${reservation.menu.name}</div>
        <div style="margin-top: 4px;">
          <a href="/admin/reservations/${reservation.id}/edit" class="btn btn-sm btn-primary">編集</a>
          <a href="/admin/reservations/${reservation.id}" class="btn btn-sm btn-danger delete-btn" data-method="delete" data-turbo-confirm="本当に削除しますか？">削除</a>
        </div>
      </div>
    `).join('')
  }

  positionPopover(popover, cell) {
    // ポップオーバーを一時的に表示してサイズを取得
    popover.style.visibility = 'hidden'
    popover.style.display = 'block'

    const cellRect = cell.getBoundingClientRect()
    const popoverRect = popover.getBoundingClientRect()

    // スクロール位置を考慮した座標計算
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    let left = cellRect.left + scrollLeft + (cellRect.width / 2) - (popoverRect.width / 2)
    let top = cellRect.top + scrollTop - popoverRect.height - 10

    // 画面端での位置調整
    if (left < scrollLeft + 10) {
      left = scrollLeft + 10
    }
    if (left + popoverRect.width > scrollLeft + window.innerWidth - 10) {
      left = scrollLeft + window.innerWidth - popoverRect.width - 10
    }

    // 上に表示できない場合は下に表示
    if (top < scrollTop + 10) {
      top = cellRect.bottom + scrollTop + 10
      // 矢印の向きを変更（上向きに）
      const arrow = popover.querySelector('.popover-arrow')
      if (arrow) {
        popover.querySelector('.popover').className = 'popover bs-popover-bottom'
      }
    } else {
      // 通常の上向き表示
      const arrow = popover.querySelector('.popover-arrow')
      if (arrow) {
        popover.querySelector('.popover').className = 'popover bs-popover-top'
      }
    }

    popover.style.position = 'absolute'
    popover.style.left = `${left}px`
    popover.style.top = `${top}px`
    popover.style.visibility = 'visible'
  }

  resetPopoverToSingleReservation(popover) {
    const popoverContainer = popover.querySelector('.popover')
    popoverContainer.innerHTML = `
      <div class="popover-arrow"></div>
      <div class="popover-header">
        <strong>予約詳細</strong>
        <button class="btn-close float-end" type="button" data-action="click->reservation-detail-popover#close"></button>
      </div>
      <div class="popover-body">
        <div class="reservation-details">
          <p>
            <strong>顧客名:</strong>
            <span class="customer-name"></span>
          </p>
          <p>
            <strong>トレーナー:</strong>
            <span class="trainer-name"></span>
          </p>
          <p>
            <strong>開始時刻:</strong>
            <span class="start-time"></span>
          </p>
          <p>
            <strong>終了時刻:</strong>
            <span class="end-time"></span>
          </p>
          <p>
            <strong>メニュー:</strong>
            <span class="menu-name"></span>
          </p>
          <div class="mt-2">
            <a href="#" class="btn btn-sm btn-primary edit-reservation-btn">編集</a>
            <a href="#" class="btn btn-sm btn-danger ms-1 delete-reservation-btn" data-confirm="本当に削除しますか？">削除</a>
          </div>
        </div>
      </div>
    `
  }

  handleOutsideClick(event) {
    const popover = document.querySelector('.reservation-detail-popover')
    if (!popover.contains(event.target)) {
      popover.style.display = 'none'
    }
  }
}
